'use client'

import { useState } from 'react'

import type { MenuItem, MenuSeparator, NavGroup } from '@graftcode/design-system'
import { Icon, IconComponent } from '@graftcode/design-system'

import { AppVariant } from '../../../../enums'
import { BaseMobileMenu } from '../../BaseMobileMenu'
import { AddNewDrawer } from '../../components'

import styles from '../../MobileMenu.module.scss'

interface PortalMobileMenuProps {
  /** Whether the menu is open */
  isOpen: boolean
  /** Callback to close the menu */
  onClose: () => void
  /** Navigation groups */
  menuGroups: NavGroup[]
  /** Items for the Help dropdown / drawer */
  helpItems?: (MenuItem | MenuSeparator)[]
  /** Label for add new button */
  addNewLabel?: string
  /** Hide the add new button (e.g. on settings pages) */
  hideAddNew?: boolean
  /** Handler for profile click */
  onProfileClick?: (itemId: string) => void
  /** Handler for help item click */
  onHelpClick?: (itemId: string) => void
  /** Handler for navigation click */
  onNavigationClick?: (url: string) => void
  /** Handler for project selection */
  onProjectClick?: (projectId: string) => void
  /** Handler for workspace selection */
  onWorkspaceClick?: (workspaceId: string) => void
  /** Handler for add service click */
  onAddServiceClick?: () => void
  /** Handler for add project click */
  onAddProjectClick?: () => void
  /** Handler for add workspace click */
  onAddWorkspaceClick?: () => void
  /** Handler to open workspace settings */
  onOpenWorkspaceSettings?: () => void
  /** Handler to open project settings */
  onOpenProjectSettings?: () => void
  /** Handler to open project members */
  onOpenProjectMembers?: () => void
  /** Handler to trigger project invite flow */
  onInviteProjectClick?: () => void
  /** Handler to trigger workspace invite flow */
  onInviteWorkspaceClick?: () => void
  /** Handler to open create project flow */
  onCreateProject?: () => void
  notificationBadge?: number
  onNotificationClick?: () => void
}

export function PortalMobileMenu({
  isOpen,
  onClose,
  menuGroups,
  helpItems,
  addNewLabel = 'Add new',
  hideAddNew = false,
  onProfileClick,
  onHelpClick,
  onNavigationClick,
  onProjectClick,
  onWorkspaceClick,
  onAddServiceClick,
  onAddProjectClick,
  onAddWorkspaceClick,
  onOpenWorkspaceSettings,
  onOpenProjectSettings,
  onOpenProjectMembers,
  onInviteProjectClick,
  onInviteWorkspaceClick,
  onCreateProject,
  notificationBadge,
  onNotificationClick,
}: PortalMobileMenuProps) {
  const [isAddNewDrawerOpen, setIsAddNewDrawerOpen] = useState(false)

  const handleAddNewClick = () => setIsAddNewDrawerOpen(true)

  const handleAddNewDrawerClose = () => setIsAddNewDrawerOpen(false)

  const handleAddService = () => {
    onAddServiceClick?.()
    onClose()
  }

  const handleAddProject = () => {
    onAddProjectClick?.()
    onClose()
  }

  const handleAddWorkspace = () => {
    onAddWorkspaceClick?.()
    onClose()
  }

  const navigationTopSlot = hideAddNew ? null : (
    <button className={styles.addNewButton} onClick={handleAddNewClick}>
      <IconComponent icon={Icon.AddFilled} size={24} />
      <span>{addNewLabel}</span>
    </button>
  )

  return (
    <>
      <BaseMobileMenu
        isOpen={isOpen}
        onClose={onClose}
        appVariant={AppVariant.PORTAL}
        menuGroups={menuGroups}
        helpItems={helpItems}
        onProfileClick={onProfileClick}
        onHelpClick={onHelpClick}
        onNavigationClick={onNavigationClick}
        onProjectClick={onProjectClick}
        onWorkspaceClick={onWorkspaceClick}
        onOpenWorkspaceSettings={onOpenWorkspaceSettings}
        onOpenProjectSettings={onOpenProjectSettings}
        onOpenProjectMembers={onOpenProjectMembers}
        onInviteProjectClick={onInviteProjectClick}
        onInviteWorkspaceClick={onInviteWorkspaceClick}
        onCreateProject={onCreateProject}
        notificationBadge={notificationBadge}
        onNotificationClick={onNotificationClick}
        navigationTopSlot={navigationTopSlot}
      />

      <AddNewDrawer
        isOpen={isAddNewDrawerOpen}
        onClose={handleAddNewDrawerClose}
        onAddServiceClick={handleAddService}
        onAddProjectClick={handleAddProject}
        onAddWorkspaceClick={handleAddWorkspace}
      />
    </>
  )
}
